<script setup lang="ts">
import Swiper from 'swiper'
import { onMounted, onUnmounted, ref, type PropType, computed } from 'vue'
import { useBreakpoints } from '@/lib/utils'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import type { ShoppingDestinationItem } from '@/assets/ts/pages/index'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const { isTabletPortrait } = useBreakpoints()

const props = defineProps({
	data: {
		type: Array as PropType<ShoppingDestinationItem[]>,
		default: () => [],
	},
	content: {
		type: Object,
		default: () => ({}),
	},
})

const swiperEl = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null
const firstSlideEl = ref<HTMLElement | null>(null)
const isFirstSlideVisible = ref(true)
const isShowLeftShadow = computed(
	() => !isFirstSlideVisible.value && isTabletPortrait.value,
)
const lastSlideEl = ref<HTMLElement | null>(null)
const isLastSlideVisible = ref(true)
const isShowRightShadow = computed(
	() => !isLastSlideVisible.value && isTabletPortrait.value,
)

const swiperOptions = {
	slidesPerView: 'auto' as const,
	spaceBetween: 20,
	speed: 500,
	allowTouchMove: true,
	breakpoints: {
		1280: {
			allowTouchMove: false,
		},
	},
}

const goPrev = () => {
	swiper?.slidePrev(500)
}

const goNext = () => {
	swiper?.slideNext(500)
}

const initializeSwiper = () => {
	if (!swiperEl.value) return
	swiper = new Swiper(swiperEl.value, swiperOptions)
}

const handleBtnClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: 'Calculate Shipment Rate',
	})
}

let firstSlideObserver: IntersectionObserver | null = null
let lastSlideObserver: IntersectionObserver | null = null

const initializeObservers = () => {
	firstSlideObserver = new IntersectionObserver(
		([entry]) => {
			isFirstSlideVisible.value = entry.isIntersecting
		},
		{ threshold: 0.5, rootMargin: '100% 0px' },
	)

	lastSlideObserver = new IntersectionObserver(
		([entry]) => {
			isLastSlideVisible.value = entry.isIntersecting
		},
		{ threshold: 0.5, rootMargin: '100% 0px' },
	)

	if (firstSlideEl.value) firstSlideObserver.observe(firstSlideEl.value)
	if (lastSlideEl.value) lastSlideObserver.observe(lastSlideEl.value)
}

onMounted(() => {
	initializeSwiper()
	initializeObservers()
})

onUnmounted(() => {
	swiper?.destroy()
	firstSlideObserver?.disconnect()
	lastSlideObserver?.disconnect()
})
</script>

<template>
	<div class="bg-t-900">
		<div class="bs-container py-distant-acquaintances">
			<div
				class="col-span-12 flex flex-col mdp:flex-row mdp:items-center mdp:gap-20px"
			>
				<div
					class="flex-none text-center text-white mdp:w-[221px] mdp:text-left mdl:w-[307px] lg:w-[380px]"
				>
					<div class="typography-h2-str mb-close-friends">
						{{ content.title }}
					</div>
					<div class="typography-b2-reg">
						{{ content.description }}
					</div>

					<div class="mt-casual-friends hidden mdp:block">
						<a :href="content.buttonLink" @click="handleBtnClick">
							<BaseButton size="large">
								{{ content.buttonText }}
							</BaseButton>
						</a>
					</div>
				</div>

				<div
					class="mx-[-20px] flex-1 overflow-hidden py-acquaintances mdp:ml-0 mdp:mr-[-32px] lg:mr-0"
				>
					<div
						ref="swiperEl"
						class="swiper relative flex-none select-none !px-[20px] mdp:!pl-0 mdp:!pr-[32px] lg:!pr-0"
					>
						<div class="swiper-wrapper">
							<div
								v-for="(item, index) in data"
								:key="index"
								:ref="
									el => {
										if (index === 0) firstSlideEl = el as HTMLElement
										if (index === data.length - 1)
											lastSlideEl = el as HTMLElement
									}
								"
								class="swiper-slide !h-[220px] !w-[172px] overflow-hidden rounded-[12px] bg-[rgba(255,255,255,0.1)]"
							>
								<div
									class="typography-h5-str mb-4px py-16px text-center text-white"
								>
									{{ item.title }}
								</div>
								<img
									:src="item.img"
									alt="Shopping destination image"
									loading="lazy"
									class="h-full w-full object-cover [clip-path:path('M0_86C0_38.5035_38.5035_0_86_0V0C133.496_0_172_38.5035_172_86V160H0V86Z')]"
								/>
							</div>
						</div>

						<div
							v-show="isShowLeftShadow"
							class="absolute left-0 top-0 z-10 h-[240px] w-[160px] rotate-180 transition-opacity duration-300 [background:linear-gradient(90deg,rgba(0,48,54,0)_0%,#183e44_100%)]"
						>
							<BaseIcon
								name="chevron_round"
								class="absolute right-0 top-1/2 hidden h-[40px] w-[40px] -translate-y-1/2 cursor-pointer lg:block"
								@click="goPrev"
							/>
						</div>
						<div
							v-show="isShowRightShadow"
							class="absolute right-0 top-0 z-10 h-[240px] w-[160px] transition-opacity duration-300 [background:linear-gradient(90deg,rgba(0,48,54,0)_0%,#183e44_100%)]"
						>
							<BaseIcon
								name="chevron_round"
								class="absolute right-0 top-1/2 hidden h-[40px] w-[40px] -translate-y-1/2 cursor-pointer lg:block"
								@click="goNext"
							/>
						</div>
					</div>
				</div>

				<div class="grid place-content-center mdp:hidden">
					<a :href="content.buttonLink" @click="handleBtnClick">
						<BaseButton size="large">
							{{ content.buttonText }}
						</BaseButton>
					</a>
				</div>
			</div>
		</div>
	</div>
</template>
